import { createLogic } from 'redux-logic';
import { order as orderApi } from '@squareup/sq-addons-web-sdk';

import {
  ADD_DISCOUNT,
  CLEAR_DISCOUNTS,
  FETCH_DISCOUNTS,
  REMOVE_DISCOUNT,
  REMOVE_DISCOUNTS,
  SUBMIT_DISCOUNTS,
} from './types';
import {
  addDiscountFulfilled,
  addDiscountRejected,
  clearDiscountsFulfilled,
  clearDiscountsRejected,
  fetchDiscountsFulfilled,
  fetchDiscountsRejected,
  removeDiscountFulfilled,
  removeDiscountRejected,
  removeDiscountsFulfilled,
  removeDiscountsRejected,
  submitDiscountsFulfilled,
  submitDiscountsRejected,
} from './actions';
import { setPointBalance } from 'store/customer';
import { simulateAccrualAndRedemption } from 'store/api/discounts/simulateAccrualAndRedemption';
import { postAndAccruePointswithRedeem } from 'store/api/discounts/postAndAccruePointsWithRedeem';

export const addDiscountLogic = createLogic({
  type: ADD_DISCOUNT,
  cancelType: CLEAR_DISCOUNTS,

  async process({ action, getState, cancelled$ }, dispatch, done) {
    let cancelled = false;
    let discountId = '';

    cancelled$.subscribe(async () => {
      cancelled = true;
    });

    const posContext = getState().context.posContext;
    const pxContext = getState().context.pxContext;
    const customer = getState().customer.customer;

    const discount = action.payload.discount;
    discount.metadata.pxCardId = customer?.referenceId;

    if (customer) {
      try {
        // Add the discount to the order.
        discountId = await orderApi.addDiscount(discount);
        const updatedOrder = await orderApi.retrieve();

        // Call Simulate Accrual And Redemption API to make sure discount can
        // be added.
        const response = await simulateAccrualAndRedemption(
          posContext,
          pxContext,
          updatedOrder,
          customer
        );

        const discountNotApplied = response.discountsNotApplied.find(
          (discountNotApplied) => discountNotApplied.discount.uid === discountId
        );

        if (discountNotApplied) {
          throw new Error(
            `Failed to apply discount. ${discountNotApplied.reason}`
          );
        }

        if (!cancelled) {
          // Update discounts in redux.
          dispatch(
            fetchDiscountsFulfilled(
              response.availableDiscounts,
              response.appliedDiscounts,
              response.discountsNotApplied,
              response.unavailableDiscounts,
              response.updatedDiscounts
            )
          );

          dispatch(addDiscountFulfilled());
        }
      } catch (error) {
        // Remove discount from order if it was added.
        if (discountId) {
          await orderApi.removeDiscount(discountId);
        }

        dispatch(addDiscountRejected(error));
      } finally {
        done();
      }
    } else {
      dispatch(addDiscountRejected(new Error('Customer is not defined')));
    }
  },
});

export const clearDiscountsLogic = createLogic({
  type: CLEAR_DISCOUNTS,

  async process({ getState }, dispatch, done) {
    const posContext = getState().context.posContext;
    const pxContext = getState().context.pxContext;
    const customer = getState().customer.customer;

    if (customer) {
      try {
        const order = await orderApi.retrieve();
        const paytronixDiscounts = (order.discounts ?? []).filter(
          (discount) => !!discount.metadata?.pxId
        );

        // Remove all Paytronix discounts.
        for (const discount of paytronixDiscounts) {
          if (discount.uid) {
            await orderApi.removeDiscount(discount.uid);
          }
        }
        const updatedOrder = await orderApi.retrieve();

        // Call Simulate Accrual And Redemption API to get updated discounts.
        const response = await simulateAccrualAndRedemption(
          posContext,
          pxContext,
          updatedOrder,
          customer
        );

        // Update discounts in redux.
        dispatch(
          fetchDiscountsFulfilled(
            response.availableDiscounts,
            response.appliedDiscounts,
            response.discountsNotApplied,
            response.unavailableDiscounts,
            response.updatedDiscounts
          )
        );

        dispatch(clearDiscountsFulfilled());
      } catch (error) {
        dispatch(clearDiscountsRejected(error));
      } finally {
        done();
      }
    } else {
      dispatch(clearDiscountsRejected(new Error('Customer is not defined')));
    }
  },
});

export const fetchDiscountsLogic = createLogic({
  type: FETCH_DISCOUNTS,

  async process({ getState }, dispatch, done) {
    const posContext = getState().context.posContext;
    const pxContext = getState().context.pxContext;
    const customer = getState().customer?.customer;

    const order = await orderApi.retrieve();

    if (order && customer) {
      try {
        const response = await simulateAccrualAndRedemption(
          posContext,
          pxContext,
          order,
          customer
        );
        dispatch(
          fetchDiscountsFulfilled(
            response.availableDiscounts,
            response.appliedDiscounts,
            response.discountsNotApplied,
            response.unavailableDiscounts,
            response.updatedDiscounts
          )
        );
        dispatch(setPointBalance(response.pointBalance));
      } catch (error) {
        dispatch(fetchDiscountsRejected(error));
      } finally {
        done();
      }
    } else {
      if (!order) {
        dispatch(fetchDiscountsRejected(new Error('Order is not defined')));
      }
      if (!customer) {
        dispatch(fetchDiscountsRejected(new Error('Customer is not defined')));
      }
      done();
    }
  },
});

export const removeDiscountLogic = createLogic({
  type: REMOVE_DISCOUNT,
  cancelType: CLEAR_DISCOUNTS,

  async process({ action, getState, cancelled$ }, dispatch, done) {
    let cancelled = false;

    const posContext = getState().context.posContext;
    const pxContext = getState().context.pxContext;
    const customer = getState().customer.customer;

    cancelled$.subscribe(() => {
      cancelled = true;
    });

    if (customer) {
      try {
        await orderApi.removeDiscount(action.payload.discountId);
        const updatedOrder = await orderApi.retrieve();

        // Call Simulate Accrual And Redemption API to update available
        // discount.
        const response = await simulateAccrualAndRedemption(
          posContext,
          pxContext,
          updatedOrder,
          customer
        );

        if (!cancelled) {
          // Update discounts in redux.
          dispatch(
            fetchDiscountsFulfilled(
              response.availableDiscounts,
              response.appliedDiscounts,
              response.discountsNotApplied,
              response.unavailableDiscounts,
              response.updatedDiscounts
            )
          );
        }

        dispatch(removeDiscountFulfilled());
      } catch (error) {
        dispatch(removeDiscountRejected(error));
      } finally {
        done();
      }
    } else {
      dispatch(removeDiscountRejected(new Error('Customer is not defined')));
    }
  },
});

export const removeDiscountsLogic = createLogic({
  type: REMOVE_DISCOUNTS,
  cancelType: CLEAR_DISCOUNTS,

  async process({ action, getState, cancelled$ }, dispatch, done) {
    let cancelled = false;

    const posContext = getState().context.posContext;
    const pxContext = getState().context.pxContext;
    const customer = getState().customer.customer;

    cancelled$.subscribe(() => {
      cancelled = true;
    });

    if (customer) {
      try {
        action.payload.discountIds.forEach(async (discountId) => {
          await orderApi.removeDiscount(discountId);
        });
        const updatedOrder = await orderApi.retrieve();

        // Call Simulate Accrual And Redemption API to update available
        // discount.
        const response = await simulateAccrualAndRedemption(
          posContext,
          pxContext,
          updatedOrder,
          customer
        );

        if (!cancelled) {
          // Update discounts in redux.
          dispatch(
            fetchDiscountsFulfilled(
              response.availableDiscounts,
              response.appliedDiscounts,
              response.discountsNotApplied,
              response.unavailableDiscounts,
              response.updatedDiscounts
            )
          );
        }

        dispatch(removeDiscountsFulfilled());
      } catch (error) {
        dispatch(removeDiscountsRejected(error));
      } finally {
        done();
      }
    } else {
      dispatch(removeDiscountsRejected(new Error('Customer is not defined')));
    }
  },
});

export const submitDiscountsLogic = createLogic({
  type: SUBMIT_DISCOUNTS,

  async process({ getState }, dispatch, done) {
    const posContext = getState().context.posContext;
    const pxContext = getState().context.pxContext;
    const customer = getState().customer?.customer;

    if (customer) {
      const order = await orderApi.retrieve();

      try {
        const response = await postAndAccruePointswithRedeem(
          posContext,
          pxContext,
          order,
          customer
        );

        response.result === 'success'
          ? dispatch(submitDiscountsFulfilled(response.receiptText))
          : dispatch(submitDiscountsRejected(new Error(response.errorMessage)));
      } catch (error) {
        dispatch(submitDiscountsRejected(error));
      } finally {
        done();
      }
    } else {
      dispatch(submitDiscountsRejected(new Error('Customer is not defined')));
      done();
    }
  },
});
